import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useFirm } from '../database/hooks/hooks';
import { getGoogleCredentials } from '@/cloudFunctions/googleWorkspace';
import { getMicrosoftCredentials } from '@/cloudFunctions/microsoft';

export type EmailAuthStatus =
  | { status: 'loading' }
  | { status: 'authenticated'; email: string; provider: 'google' | 'microsoft' }
  | { status: 'unauthenticated'; provider: 'google' | 'microsoft' | null };

export interface EmailAuthStatusContextValue {
  authStatus: EmailAuthStatus;
  refreshAuthStatus: () => Promise<void>;
}

const EmailAuthStatusContext = createContext<
  EmailAuthStatusContextValue | undefined
>(undefined);

export const EmailAuthStatusProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { firm } = useFirm();
  const [authStatus, setAuthStatus] = useState<EmailAuthStatus>({
    status: 'loading',
  });

  const checkAuthStatus = useCallback(async () => {
    if (!firm?.emailProvider) {
      setAuthStatus({ status: 'unauthenticated', provider: null });
      return;
    }

    try {
      if (firm.emailProvider === 'google') {
        const result = await getGoogleCredentials();
        if (result.status === 'success') {
          setAuthStatus({
            status: 'authenticated',
            email: result.payload.email,
            provider: 'google',
          });
        } else {
          setAuthStatus({ status: 'unauthenticated', provider: 'google' });
        }
      } else if (firm.emailProvider === 'microsoft') {
        const result = await getMicrosoftCredentials();
        if (result.status === 'success') {
          setAuthStatus({
            status: 'authenticated',
            email: result.payload.credentials.account.username,
            provider: 'microsoft',
          });
        } else {
          setAuthStatus({ status: 'unauthenticated', provider: 'microsoft' });
        }
      }
    } catch (error) {
      setAuthStatus({
        status: 'unauthenticated',
        provider: firm.emailProvider as 'google' | 'microsoft',
      });
    }
  }, [firm?.emailProvider]);

  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  // Memoize so unrelated firm-doc changes (any field other than
  // emailProvider) do not propagate as new context-value references and
  // re-render every consumer.
  const value = useMemo(
    () => ({ authStatus, refreshAuthStatus: checkAuthStatus }),
    [authStatus, checkAuthStatus],
  );

  return (
    <EmailAuthStatusContext.Provider value={value}>
      {children}
    </EmailAuthStatusContext.Provider>
  );
};

export const useEmailAuthStatusContext = () => {
  const value = useContext(EmailAuthStatusContext);
  if (!value) {
    throw new Error(
      'useEmailAuthStatus must be used within an EmailAuthStatusProvider',
    );
  }
  return value;
};
